/**
 * The one plain sentence for a plan limit, and the one Billing path its button goes to. The API, the portal
 * (banner, Needs you) and the chat teammate all import this, so the words and the link never drift apart.
 */

/**
 * The allowances that stop or hold something back: portal runs, runner minutes, storage, concurrent runs, and the
 * inbound mail a mailbox takes in a UTC day (`mail_inbound`, #805), counted on the enterprise's busiest mailbox.
 */
export type PlanLimitNoticeUnit = 'portal_run' | 'runner_minute' | 'storage' | 'concurrency' | 'mail_inbound'

/** `near` is from 80% of the limit up to just below it; `reached` is at or past it. */
export type PlanLimitNoticeState = 'near' | 'reached'

/** Standard speaks plain words; Advanced adds the exact counters and the UTC reset. */
export type PlanLimitNoticeMode = 'standard' | 'advanced'

export interface PlanLimitNoticeInput {
    unit: PlanLimitNoticeUnit
    used: number
    limit: number
    /** The plan by its product name ("Free"). */
    plan: string
    /**
     * The end of the current billing period as an ISO instant (the next UTC midnight for `mail_inbound`); null for
     * storage and concurrency.
     */
    resetsAt: string | null
    state: PlanLimitNoticeState
    mode: PlanLimitNoticeMode
    /** `mail_inbound` only: messages held for the enterprise's mailboxes over their daily limit (#805). */
    held?: number
    /**
     * Whether the reader can upgrade: their access includes Billing, where the button goes. False words the upgrade as
     * something to ask the owner of this enterprise for, so a reader without Billing is never told to do what they
     * cannot. Omitted is true: the API's own sentence (Needs you, the email, chat) is for whoever can act on it.
     */
    canUpgrade?: boolean
}

export interface PlanLimitNotice {
    sentence: string
    buttonLabel: 'See usage' | 'Upgrade'
    /** The portal path the button goes to: {@link planLimitButtonPath} of its label. */
    buttonPath: string
}

/**
 * The one Billing path every plan-limit button goes to: the Billing page with plan and usage (#1615), live at
 * `/billing` since #1963.
 */
export const PLAN_LIMIT_BILLING_PATH = '/billing'

/** Billing's plans, where an Upgrade button lands: the page scrolls to them rather than opening at its top. */
export const PLAN_LIMIT_PLANS_PATH = '/billing#plans'

/** Where a plan-limit button goes: an Upgrade to Billing's plans, See usage to the top of Billing, where usage is. */
export function planLimitButtonPath(label: 'See usage' | 'Upgrade'): string {
    return label === 'Upgrade' ? PLAN_LIMIT_PLANS_PATH : PLAN_LIMIT_BILLING_PATH
}

/** "Near" starts at this share of the limit. */
export const PLAN_LIMIT_NEAR_RATIO = 0.8

/** One gigabyte as storage limits count it: 1024³ bytes. */
const GB = 1024 ** 3

const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
]

/** The reset as a reader's date, "1 November": the UTC day, never shifted to the reader's own (as Blockers says it). */
function resetDay(resetsAt: string | null): string {
    const at = resetsAt === null ? null : new Date(resetsAt)
    if (at === null || Number.isNaN(at.getTime())) {
        return 'the start of next month'
    }
    return `${at.getUTCDate()} ${MONTHS[at.getUTCMonth()]}`
}

/** Bytes as gigabytes to one decimal, "0.2"; under a tenth reads "under 0.1". */
function gigabytes(bytes: number): string {
    const gb = Math.max(0, bytes) / GB
    return gb < 0.1 ? 'under 0.1' : String(Number(gb.toFixed(1)))
}

/** Who upgrades, for a reader without Billing: the owner, as the portal names them elsewhere. */
const ASK_THE_OWNER = 'ask the owner of this enterprise to upgrade'

/** "free up space or upgrade", or, for a reader who cannot upgrade, who to ask instead. */
function freeUpOrUpgrade(input: PlanLimitNoticeInput): string {
    return input.canUpgrade === false ? `free up space, or ${ASK_THE_OWNER}` : 'free up space or upgrade'
}

/** When held mail arrives, and what to do for it to arrive sooner, or who to ask (#805). */
function heldMailArrives(
    input: PlanLimitNoticeInput,
    subject: 'They arrive' | 'It arrives' | 'Held mail arrives',
): string {
    const act = input.canUpgrade === false ? ASK_THE_OWNER : 'upgrade now'
    return `${subject} as the daily limit frees up, or at once when the plan goes up: ${act}.`
}

/** "or upgrade now", or, for a reader who cannot upgrade, who to ask instead. */
function orUpgrade(input: PlanLimitNoticeInput): string {
    return input.canUpgrade === false ? `or ${ASK_THE_OWNER}` : 'or upgrade now'
}

/** The plain sentence for the unit near its limit. */
function nearSentence(input: PlanLimitNoticeInput): string {
    const left = Math.max(0, input.limit - input.used)
    const plan = `Your ${input.plan} plan`
    switch (input.unit) {
        case 'portal_run':
            return `${plan} has used most of its runs this month: ${left} left. Runs reset on ${resetDay(input.resetsAt)}.`
        case 'runner_minute':
            return `${plan} has used most of its runner minutes this month: ${left} left. Minutes reset on ${resetDay(input.resetsAt)}.`
        case 'storage':
            return `${plan}'s storage is nearly full: ${gigabytes(input.limit - input.used)} GB left. Storage does not reset by itself: ${freeUpOrUpgrade(input)}.`
        case 'concurrency':
            return `${plan} is close to its limit of runs at once: ${left} more can start. After that, new runs wait in line and start as soon as a running one finishes.`
        case 'mail_inbound':
            return `${plan} has a mailbox that has nearly reached today's mail limit: ${left} more ${left === 1 ? 'message' : 'messages'} can arrive today. The limit resets at midnight UTC.`
    }
}

/** The plain sentence for the unit at its limit: what is paused, what still works, when it resets. */
function reachedSentence(input: PlanLimitNoticeInput): string {
    const plan = `Your ${input.plan} plan`
    switch (input.unit) {
        case 'portal_run':
            return `${plan} has used all its runs this month, so new runs are paused. Chat, Needs you and everything you have set up still work. Runs reset on ${resetDay(input.resetsAt)}, ${orUpgrade(input)}.`
        case 'runner_minute':
            return `${plan} has used all its runner minutes this month, so runs that use a workspace are paused. Other runs, chat and Needs you still work. Minutes reset on ${resetDay(input.resetsAt)}, ${orUpgrade(input)}.`
        case 'storage':
            return `${plan}'s storage is full, so new runs are paused. Chat and the files you already have still work. Storage does not reset by itself: ${freeUpOrUpgrade(input)}.`
        case 'concurrency':
            return `${plan} is running as many runs at once as it can, so new runs wait in line. They start as soon as a running one finishes, and everything else still works.`
        case 'mail_inbound': {
            const held = input.held ?? 0
            if (held > 0) {
                const messages = held === 1 ? '1 message' : `${held} messages`
                return `${plan} is holding ${messages} for a mailbox over today's mail limit. ${heldMailArrives(input, held === 1 ? 'It arrives' : 'They arrive')}`
            }
            return `${plan} has a mailbox that has reached today's mail limit, so new mail to it is held, never bounced. ${heldMailArrives(input, 'Held mail arrives')}`
        }
    }
}

/** The exact counters Advanced adds: unit, used / limit, plan, any held mail and the UTC reset instant. */
function counters(input: PlanLimitNoticeInput): string {
    const reset = input.resetsAt === null ? 'does not reset' : `resets ${input.resetsAt}`
    const held = input.held ? `, ${input.held} held` : ''
    return `(${input.unit} ${input.used} / ${input.limit} on ${input.plan}${held}, ${reset})`
}

/**
 * Whether a limit in this state has paused work: a reached limit has, except concurrency and mail. A full set of runs
 * at once only makes new runs wait in line for a slot, which is what a plan with one run at a time does whenever a run
 * is in progress, so it is a calm notice the person can put away, never a blocking one that asks for an upgrade. A
 * mailbox at its daily cap holds its new mail (#805), and every run carries on.
 */
export function planLimitPauses(unit: PlanLimitNoticeUnit, state: PlanLimitNoticeState | PlanAllowanceState): boolean {
    return planLimitHolds(unit, state) && unit !== 'mail_inbound'
}

/**
 * Whether a limit in this state holds something back until it clears: work it has paused ({@link planLimitPauses}),
 * or a mailbox's new mail, held until the plan goes up (#805). Such a limit is the banner's blocking tone and the
 * picker's magenta flag, stays until it clears, and asks for an upgrade. Full runs at once holds nothing back.
 */
export function planLimitHolds(unit: PlanLimitNoticeUnit, state: PlanLimitNoticeState | PlanAllowanceState): boolean {
    return state === 'reached' && unit !== 'concurrency'
}

/** "Upgrade" where the limit holds something back; "See usage" where it is coming close, or runs only wait for a slot. */
export function planLimitButtonLabel(
    unit: PlanLimitNoticeUnit,
    state: PlanLimitNoticeState | PlanAllowanceState,
): 'See usage' | 'Upgrade' {
    return planLimitHolds(unit, state) ? 'Upgrade' : 'See usage'
}

/**
 * The sentence and button for one allowance near or at its limit. Standard is plain words only: no code, unit name or
 * run id. Advanced is the same words with the exact counters and the UTC reset after them.
 */
export function planLimitNotice(input: PlanLimitNoticeInput): PlanLimitNotice {
    const words = input.state === 'reached' ? reachedSentence(input) : nearSentence(input)
    const buttonLabel = planLimitButtonLabel(input.unit, input.state)
    return {
        sentence: input.mode === 'advanced' ? `${words} ${counters(input)}` : words,
        buttonLabel,
        buttonPath: planLimitButtonPath(buttonLabel),
    }
}

/** `near` from 80% of the limit up to just below it; `reached` at or past it; `ok` otherwise (unlimited, overage). */
export type PlanAllowanceState = 'ok' | 'near' | 'reached'

/** One of the enterprise's allowances, as the portal banner, Needs you and chat read it. */
export interface PlanAllowance {
    unit: PlanLimitNoticeUnit
    used: number
    /** The plan's figure (bytes for storage); null is unlimited. */
    limit: number | null
    state: PlanAllowanceState
    /**
     * The end of the current billing period for monthly units, the next UTC midnight for `mail_inbound`; null for
     * storage and concurrency.
     */
    resetsAt: string | null
    /** The plan by its product name. */
    plan: string
    /**
     * `mail_inbound` only: messages held for the enterprise's mailboxes over their daily limit, not yet released
     * (#805). While any are, the allowance reads `reached`, past midnight too ({@link mailInboundAllowanceState}).
     */
    held?: number
    buttonLabel: 'See usage' | 'Upgrade'
    buttonPath: string
}

/**
 * The one Needs you item a reached plan limit makes, however many runs it paused (#2122): `GET /gates/plan-limits`
 * sends these to the Needs you page beside the waiting gates, and the hourly email counts each as one thing.
 */
export interface PlanLimitNeedsYouItem {
    unit: PlanLimitNoticeUnit
    /** The shared reached sentence, in Standard wording. */
    sentence: string
    buttonLabel: 'Upgrade'
    buttonPath: string
    /**
     * When the limit episode began: the earliest current pause (each run's latest) among the runs it holds now. One
     * episode is one item, so a later pause in it, or asking again, never makes it new.
     */
    since: string
    /** The allowance the item is about, so a reader in Advanced can show its exact counters. */
    allowance: PlanAllowance
}

/**
 * The state of `used` against `limit`: `reached` at or past it, `near` from {@link PLAN_LIMIT_NEAR_RATIO} of it (whole),
 * `ok` below that, and always `ok` for an unlimited figure or a unit that does not stop (billed as overage).
 */
export function planAllowanceState(used: number, limit: number | null, stops: boolean): PlanAllowanceState {
    if (!stops || limit === null) {
        return 'ok'
    }
    if (used >= limit) {
        return 'reached'
    }
    // Floored, so a figure that cannot be split evenly (bytes, odd minutes) turns near at its whole 80%; never at
    // nothing used, so a limit of one (Free's one run at a time) is ok until it is reached.
    return used >= Math.max(1, Math.floor(PLAN_LIMIT_NEAR_RATIO * limit)) ? 'near' : 'ok'
}

/**
 * The state of the `mail_inbound` allowance (#805): `reached` while any of the enterprise's mail is held, whatever the
 * busiest mailbox has taken today, so the warning does not go quiet at midnight with mail still held; otherwise the
 * busiest mailbox's count today (`used`) against the cap, as any allowance reads.
 */
export function mailInboundAllowanceState(used: number, cap: number, held: number): PlanAllowanceState {
    return held > 0 ? 'reached' : planAllowanceState(used, cap, true)
}

/**
 * The allowance's notice in `mode`, or null when it is `ok` (or unlimited): nothing to say. `canUpgrade` false is a
 * reader whose access does not include Billing: the words then say who to ask (see {@link PlanLimitNoticeInput}).
 */
export function planAllowanceNotice(
    allowance: PlanAllowance,
    mode: PlanLimitNoticeMode,
    canUpgrade = true,
): PlanLimitNotice | null {
    if (allowance.state === 'ok' || allowance.limit === null) {
        return null
    }
    return planLimitNotice({
        unit: allowance.unit,
        used: allowance.used,
        limit: allowance.limit,
        plan: allowance.plan,
        resetsAt: allowance.resetsAt,
        state: allowance.state,
        mode,
        canUpgrade,
        ...(allowance.held === undefined ? {} : { held: allowance.held }),
    })
}

/**
 * The one allowance a banner shows, of many: a limit that holds something back first (paused work, or held mail), then
 * one coming close, then runs at once (near or full: new runs only wait for a slot, so it never hides a warning about
 * the month's runs), and within each the highest share of its limit wins. Null when none is near or reached. `skip`
 * leaves out an allowance the person has put away (a dismissed banner).
 */
export function topPlanAllowance(
    allowances: readonly PlanAllowance[],
    skip: (allowance: PlanAllowance) => boolean = () => false,
): PlanAllowance | null {
    const rank = (item: PlanAllowance) => {
        if (planLimitHolds(item.unit, item.state)) {
            return 3
        }
        return item.unit === 'concurrency' ? 1 : 2
    }
    const ratio = (item: PlanAllowance) => (item.limit ? item.used / item.limit : 0)
    const shown = allowances.filter(item => item.state !== 'ok' && item.limit !== null && !skip(item))
    shown.sort((a, b) => rank(b) - rank(a) || ratio(b) - ratio(a))
    return shown[0] ?? null
}
